import FW from '../fw/fw';
import Env from '../fw/env';
import Logger from '../fw/logger';
import ID3 from '../streaming/id3';
import HlsJS from '../streaming/hls-js/hls-js';
import ShakaPlayer from '../streaming/shaka-player/shaka-player';
import PDQuality from '../modules/pd-quality';
import Speed from '../modules/speed';
import Pip from '../modules/pip';
import Floating from '../modules/floating';
import Sharing from '../modules/sharing';
import ExtraModule from '../modules/extra';
import AudioOnly from '../audio-only/audio-only';
import Chapters from '../accessibility/chapters';


export default class DomManagement {

  #rmp;
  #touchRmp = false;
  #onMobileStartAutoHideCentralUIFn = null;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  #containerTouch(event) {
    if (event) {
      event.stopPropagation();
    }
    Logger.print('info', 'touchend on container');
    this.#touchRmp = true;
    this.#rmp.rmpUIManagement.showChrome();
  }

  #containerClick(event) {
    if (!this.#rmp.playerReady) {
      Logger.print('info', 'prevented click event on container because player is not ready yet');
      return;
    }
    if (!this.#touchRmp) {
      if (event) {
        event.stopPropagation();
      }
      // when linear ads playing click on container must be prevented (open ads)
      if (this.#rmp.linearAdOnStage) {
        return;
      }
      // 360 video after player has first started prevent click on container 
      // to allow 360 UI to run
      if (this.#rmp.video360 && !this.#rmp.rmpPlaybackManagement.firstPlay) {
        return;
      }
      Logger.print('info', 'click event on container');
      this.#rmp.initiatePlayback();
    }
  }

  #containerDBClick(event) {
    if (event) {
      event.stopPropagation();
    }
    if (this.#rmp.isInFullscreen) {
      this.#rmp.setFullscreen(false).catch(error => {
        this.#rmp.rmpUtils.warning(1017, error);
      });
    } else {
      this.#rmp.setFullscreen(true).catch(error => {
        this.#rmp.rmpUtils.warning(1017, error);
      });
    }
  }

  #outlineInteraction(event) {
    FW.eventStopPrevent(event);
    this.#rmp.rmpUIManagement.showChrome();
  }

  #onMobileStartAutoHideCentralUI() {
    this.#rmp.off('playing adloaded aderror', this.#onMobileStartAutoHideCentralUIFn);
    this.#rmp.dom.centralMobileUI.classList.remove('rmp-mobile-show-play');
  }

  #appendMobileCentralUI() {
    this.#rmp.dom.centralMobileUI = FW.createElement(
      'button',
      'rmp-button rmp-mobile-central-ui rmp-mobile-show-play rmp-color-bg',
      null,
      null
    );
    this.#rmp.rmpAccessible.button(this.#rmp.dom.centralMobileUI, this.#rmp.labels.hint.play, true);
    this.#rmp.dom.centralMobileUIIcon = FW.createElement('span', 'rmp-i rmp-i-play');
    this.#rmp.dom.centralMobileUI.appendChild(this.#rmp.dom.centralMobileUIIcon);
    this.#rmp.dom.container.appendChild(this.#rmp.dom.centralMobileUI);
    this.#onMobileStartAutoHideCentralUIFn = this.#onMobileStartAutoHideCentralUI.bind(this);
    this.#rmp.on('playing adloaded aderror', this.#onMobileStartAutoHideCentralUIFn);
  }

  #appendCentralPlay() {
    this.#rmp.dom.overlayButton = FW.createElement(
      'button',
      'rmp-button rmp-overlay-button rmp-color-bg',
      null,
      null
    );
    this.#rmp.rmpAccessible.button(this.#rmp.dom.overlayButton, this.#rmp.labels.hint.play, true);
    this.#rmp.dom.overlayButtonIcon = FW.createElement('span', 'rmp-i rmp-i-play');
    this.#rmp.dom.overlayButton.appendChild(this.#rmp.dom.overlayButtonIcon);
    this.#rmp.dom.container.appendChild(this.#rmp.dom.overlayButton);
  }

  #appendLoadingSpin() {
    this.#rmp.dom.loadingSpin = FW.createElement('div', 'rmp-loading-spin rmp-color-bg');
    let className = 'rmp-loading-spin-circle';
    if (this.#rmp.iconSpinner === 'rmp') {
      className += ' rmp-loading-spin-circle-alt';
    }
    const circle = FW.createElement('div', className);
    this.#rmp.dom.loadingSpin.appendChild(circle);
    this.#rmp.dom.container.appendChild(this.#rmp.dom.loadingSpin);
  }

  #mediaTagError(event) {
    const errorTypes = [
      'MEDIA_ERR_CUSTOM',
      'MEDIA_ERR_ABORTED',
      'MEDIA_ERR_NETWORK',
      'MEDIA_ERR_DECODE',
      'MEDIA_ERR_SRC_NOT_SUPPORTED',
      'MEDIA_ERR_ENCRYPTED'
    ];
    if (event && event.target) {
      // https://www.w3.org/TR/html50/embedded-content-0.html#mediaerror
      // MEDIA_ERR_SRC_NOT_SUPPORTED is sign of fatal error
      // other errors may produce non-fatal error in the browser so we do not 
      // act upon them
      const videoElement = event.target;
      if (videoElement && videoElement.error && typeof videoElement.error.code === 'number') {
        const errorCode = videoElement.error.code;
        let errorMessage = '';
        if (typeof videoElement.error.message === 'string') {
          errorMessage = videoElement.error.message;
        }
        Logger.print('error', 'error on video element with code ' + errorCode.toString() + ' and message ' + errorMessage);
        if (errorTypes[errorCode]) {
          Logger.print('error', 'error type is ' + errorTypes[errorCode]);
        }
        // MEDIA_ERR_SRC_NOT_SUPPORTED (numeric value 4)
        // MEDIA_ERR_NETWORK (numeric value 2)
        if ((this.#rmp.rmpPlaybackManagement.firstFrameReached && errorCode === 2) || errorCode === 4) {
          this.#rmp.rmpUtils.error(100, event);
          return;
        }
      }
      this.#rmp.rmpUtils.warning(1010, event);
    }
  }

  #createControlBar() {
    this.#rmp.dom.controlBar = FW.createElement('div', 'rmp-control-bar rmp-color-bg');
    this.#rmp.dom.playPause = FW.createElement(
      'button',
      'rmp-play-pause rmp-button rmp-i rmp-i-play',
      null,
      null
    );
    this.#rmp.dom.playPauseHint = FW.createElement(
      'div',
      'rmp-control-bar-hint rmp-color-bg',
      this.#rmp.labels.hint.play + ' (k)',
      this.#rmp.rtl
    );
    this.#rmp.dom.playPause.appendChild(this.#rmp.dom.playPauseHint);
    this.#rmp.dom.controlBar.appendChild(this.#rmp.dom.playPause);
  }

  #createCurrentTimeDuration() {
    this.#rmp.dom.time = FW.createElement('div', 'rmp-time');
    this.#rmp.dom.timeElapsedText = FW.createElement('span', 'rmp-time-elapsed-text', null, this.#rmp.rtl);
    this.#rmp.dom.time.appendChild(this.#rmp.dom.timeElapsedText);
    if (this.#rmp.dom.volume) {
      this.#rmp.dom.controlBar.appendChild(this.#rmp.dom.time);
    }
    // append time in VolumeManagement to avoid has() support - no re-order support with CSS where no has() CSS support
  }

  prepare() {
    Logger.print('info', 'starting prepare() for player DOM');
    if (this.#rmp.readingDashShaka || this.#rmp.readingHlsJS || this.#rmp.readingHlsShaka) {
      this.#rmp.readingMse = true;
      // disable preload metadata for hls.js and Shaka player > set to auto
      /*if (this.#rmp.readingHlsJS && this.#rmp.preload === 'metadata') {
        this.#rmp.preload === 'auto';
      }*/
    }
    if (this.#rmp.readingMse || this.#rmp.readingOutstream) {
      this.#rmp.readingPDorNative = false;
    }
    if (this.#rmp.readingOutstream || this.#rmp.readingMp4 ||
      ((this.#rmp.readingM4a || this.#rmp.readingMp3 || this.#rmp.readingOgg) && !this.#rmp.audioOnlyIcecast)) {
      this.#rmp.detectLive = false;
    }

    // ima does not work on iOS with MPEG-DASH
    if (this.#rmp.ads && this.#rmp.adParser === 'ima' && this.#rmp.readingDashShaka && Env.isIos[0]) {
      this.#rmp.ads = false;
    }

    // append video tag to DOM
    this.#rmp.generateErrorFn = this.#mediaTagError.bind(this);
    if (this.#rmp.readingPDorNative) {
      this.#rmp.dom.video.addEventListener('error', this.#rmp.generateErrorFn, true);
    }
    if (this.#rmp.dom.content.contains(this.#rmp.dom.poster)) {
      this.#rmp.dom.content.insertAdjacentElement('afterbegin', this.#rmp.dom.video);
    } else {
      this.#rmp.dom.content.appendChild(this.#rmp.dom.video);
    }
    if (this.#rmp.contentMetadata.description !== '') {
      this.#rmp.dom.descriptionLabel = document.createElement('label', null, this.#rmp.contentMetadata.description);
      FW.hide(this.#rmp.dom.descriptionLabel);
      this.#rmp.dom.descriptionLabel.id = 'media-description-for-' + this.#rmp.id;
      this.#rmp.dom.content.appendChild(this.#rmp.dom.descriptionLabel);
    }
    this.#rmp.dom.container.appendChild(this.#rmp.dom.content);

    // append rmp-ad-container for Google IMA
    if (this.#rmp.ads && this.#rmp.adParser === 'ima' && !this.#rmp.adImaDai) {
      this.#rmp.dom.adContainer = FW.createElement('div', 'rmp-ad-container');
      this.#rmp.dom.container.appendChild(this.#rmp.dom.adContainer);
    }

    // append module area and module area for overlays after rmp-content
    this.#rmp.dom.moduleArea = FW.createElement('div', 'rmp-module-area');
    this.#rmp.dom.container.appendChild(this.#rmp.dom.moduleArea);
    this.#rmp.dom.moduleOverlaysArea = FW.createElement('div', 'rmp-module-overlays-area');
    this.#rmp.dom.container.appendChild(this.#rmp.dom.moduleOverlaysArea);

    // controlBar
    this.#createControlBar();

    // time
    this.#createCurrentTimeDuration();

    Logger.print('info', 'starting configuring preload mode: ' + this.#rmp.preload);
    // init hls.js or Shaka
    if (this.#rmp.readingPDorNative) {
      this.#rmp.dom.video.preload = this.#rmp.preload;
    } else if (this.#rmp.preload !== 'none') {
      if (this.#rmp.readingHlsJS) {
        if (!this.#rmp.rmpHlsJS) {
          this.#rmp.rmpHlsJS = new HlsJS(this.#rmp);
        }
        this.#rmp.rmpHlsJS.init();
        this.#rmp.msePreloadRequested = true;
      } else if (this.#rmp.readingShaka) {
        if (!this.#rmp.rmpShakaPlayer) {
          this.#rmp.rmpShakaPlayer = new ShakaPlayer(this.#rmp);
        }
        this.#rmp.rmpShakaPlayer.init();
        this.#rmp.msePreloadRequested = true;
      }
    }
    Logger.print('info', 'finished configuring preload mode: ' + this.#rmp.preload);

    // central play/pause
    if (this.#rmp.isMobile) {
      this.#appendMobileCentralUI();
    } else {
      this.#appendCentralPlay();
    }
    if (this.#rmp.hideCentralPlayButton) {
      this.#rmp.dom.container.classList.add('rmp-no-central-play');
    }
    // loading spin
    this.#appendLoadingSpin();
    if (this.#rmp.hideCentralBuffering) {
      this.#rmp.dom.container.classList.add('rmp-no-loading-spin');
    }
    // hideControls
    if (this.#rmp.hideControls) {
      this.#rmp.rmpUIManagement.hideControlsUI();
    }
    if (this.#rmp.hideExternalPlayerLabels || this.#rmp.isMobile) {
      this.#rmp.dom.container.classList.add('rmp-no-hint');
    }

    // Analytics (MUX|Bitmovin)
    if (this.#rmp.readingPDorNative) {
      // MUX DATA
      if (this.#rmp.rmpMux) {
        this.#rmp.rmpMux.initHTML5();
      }
      // Bitmovin Analytics
      if (window.bitmovin && this.#rmp.bitmovinAnalyticsConfig.key &&
        typeof this.#rmp.bitmovinAnalyticsConfig.key === 'string') {
        Logger.print('bitmovin analytics', 'adding native HTML5 video analytics support', this.#rmp.bitmovinAnalyticsConfig);
        this.#rmp.bitmovinAnalytics = new window.bitmovin.analytics.adapters.HTMLVideoElementAdapter(
          this.#rmp.bitmovinAnalyticsConfig,
          this.#rmp.dom.video
        );
        Logger.print('bitmovin analytics', 'bitmovinAnalytics', this.#rmp.bitmovinAnalytics);
      }
    }

    // ID3
    const rmpID3 = new ID3(this.#rmp);
    rmpID3.init();

    // fires htmlmediaelementappendedtodom
    this.#rmp.dispatch('htmlmediaelementappendedtodom');
  }

  #prepareIconSize() {
    const iconsSize = this.#rmp.iconsSize;
    if (iconsSize > 0 && iconsSize !== 1) {
      let style = '.rmp-control-bar .rmp-i {font-size: ' + FW.precisionRound(1.86 * iconsSize, 2) + 'em;}';
      style += '.rmp-module-overlay .rmp-i {font-size: ' + FW.precisionRound(2.14 * iconsSize, 2) + 'em;}';
      style += '.rmp-module .rmp-i {font-size: ' + FW.precisionRound(2.14 * iconsSize, 2) + 'em;}';
      style += '.rmp-module-overlay .rmp-sharing-social .rmp-i {font-size: ' +
        FW.precisionRound(3.14 * iconsSize, 2) + 'em;}';
      style += '.rmp-playlist-left-arrow, .rmp-playlist-right-arrow {font-size: ' +
        FW.precisionRound(2.42 * iconsSize, 2) + 'em;}';
      style += '.rmp-360-nav-button {font-size: ' + FW.precisionRound(2.42 * iconsSize, 2) + 'em;}';
      style += '.rmp-overlay-button .rmp-i-play, .rmp-overlay-button .rmp-i-pause, .rmp-overlay-button .rmp-i-replay, .rmp-mobile-central-ui .rmp-i-play, .rmp-mobile-central-ui .rmp-i-pause, .rmp-mobile-central-ui .rmp-i-replay {font-size: ' + 3.14 * iconsSize + 'em;}';
      style += '.rmp-quick, .rmp-quick-both {font-size: ' + FW.precisionRound(1.71 * iconsSize, 2) + 'em;}';
      style += 'google-cast-launcher {width: ' + 30 * iconsSize + 'px;}';
      FW.appendStyle(style, this.#rmp.shadowRoot);
    }
  }

  append() {
    Logger.print('info', 'starting append() for player DOM');
    if (!this.#rmp.adOutStream && !this.#rmp.tvPlayerSkin) {
      this.#rmp.dom.container.classList.add('rmp-wait-for-duration');
    }
    this.#rmp.dom.container.addEventListener('contextmenu', FW.contextMenuInteraction);
    // touch/click container
    if (!this.#rmp.tvPlayerSkin) {
      this.#rmp.dom.container.addEventListener('touchend', this.#containerTouch.bind(this));
      if (this.#rmp.pauseContentWhenViewingAreaIsClicked) {
        this.#rmp.dom.container.addEventListener('click', this.#containerClick.bind(this));
      }
      if (this.#rmp.doubleClickForFullscreen) {
        this.#rmp.dom.container.addEventListener('dblclick', this.#containerDBClick.bind(this));
      }
    }

    // VOLUME
    this.#rmp.rmpVolumeManagement.init();

    if (this.#rmp.readingMp4 || this.#rmp.readingOutstream ||
      ((this.#rmp.readingM4a || this.#rmp.readingMp3 || this.#rmp.readingOgg) && !this.#rmp.audioOnlyIcecast)) {
      this.#rmp.rmpSeekManagement.appendSeekBar();
    }


    // audio only
    if (this.#rmp.audioOnly) {
      const rmpAudioOnly = new AudioOnly(this.#rmp);
      rmpAudioOnly.init();
    }

    // fullscreen
    this.#rmp.rmpFullscreenManagement.init();

    // QUALITY 
    if (this.#rmp.readingMp4 && this.#rmp.src.mp4.length > 0) {
      this.#rmp.rmpPDQuality = new PDQuality(this.#rmp);
      this.#rmp.rmpPDQuality.init();
    }

    // SPEED 
    if (this.#rmp.speed) {
      const rmpSpeed = new Speed(this.#rmp);
      rmpSpeed.init();
    }

    // PiP
    if (this.#rmp.pip) {
      this.#rmp.rmpPip = new Pip(this.#rmp);
      this.#rmp.rmpPip.init();
    }

    // floating
    if (this.#rmp.floating) {
      this.#rmp.rmpFloating = new Floating(this.#rmp);
      this.#rmp.rmpFloating.init();
    }

    // sharing
    if (this.#rmp.sharing && this.#rmp.sharingUrl) {
      this.#rmp.rmpSharing = new Sharing(this.#rmp);
      this.#rmp.rmpSharing.init();
    }

    if (this.#rmp.loopModule || this.#rmp.infoModule || this.#rmp.theaterModeModule) {
      const rmpExtraModule = new ExtraModule(this.#rmp);
      if (this.#rmp.loopModule) {
        rmpExtraModule.initLoop();
      }
      if (this.#rmp.infoModule) {
        rmpExtraModule.initInfo();
      }
      if (this.#rmp.theaterModeModule) {
        rmpExtraModule.initTheater();
      }
    }


    // check chrome
    this.#rmp.dom.container.addEventListener(
      'mousemove',
      this.#rmp.rmpUIManagement.showChrome.bind(this.#rmp.rmpUIManagement)
    );

    // play/pause 
    this.#rmp.initiatePlayback = this.#rmp.rmpPlaybackManagement.init.bind(this.#rmp.rmpPlaybackManagement);
    FW.addEvents(['touchend', 'click'], this.#rmp.dom.playPause, this.#rmp.initiatePlayback);
    if (this.#rmp.isMobile) {
      FW.addEvents(['touchend', 'click'], this.#rmp.dom.centralMobileUI, this.#rmp.initiatePlayback);
    } else {
      FW.addEvents(['touchend', 'click'], this.#rmp.dom.overlayButton, this.#rmp.initiatePlayback);
    }

    // prevent click on the controlBar/loadingSpin to trigger play/pause (as on container)
    const _outlineInteractionFn = this.#outlineInteraction.bind(this);
    FW.addEvents(['touchend', 'click'], this.#rmp.dom.controlBar, _outlineInteractionFn);
    this.#rmp.dom.controlBar.addEventListener('dblclick', FW.stopPropagation);

    // when mouse is over controls do not auto-hide
    if (!this.#rmp.isMobile) {
      this.#rmp.dom.controlBar.addEventListener('mouseenter', event => {
        if (event) {
          event.stopPropagation();
        }
        this.#rmp.forceResetTimer = true;
      });
      this.#rmp.dom.controlBar.addEventListener('mouseleave', event => {
        if (event) {
          event.stopPropagation();
        }
        this.#rmp.forceResetTimer = false;
      });
    }

    // chapters + thumbnails + quick rewind for VOD only
    if (this.#rmp.isVod && this.#rmp.chaptersLoc !== '') {
      this.#rmp.rmpChapters = new Chapters(this.#rmp);
      this.#rmp.rmpChapters.init();
    }

    // icons size
    this.#prepareIconSize();

    // make accessible
    const keyboardShortcutLabel = this.#rmp.labels.hint.keyboardShortcut;
    const playPauseAccessibleLabel = this.#rmp.labels.hint.play + ' ' + keyboardShortcutLabel + ' k';
    this.#rmp.rmpAccessible.button(this.#rmp.dom.playPause, playPauseAccessibleLabel, true, null, 'k');

    const enterFullscreenAccessibleLabel = this.#rmp.labels.hint.enterFullscreen + ' ' + keyboardShortcutLabel + ' f';
    this.#rmp.rmpAccessible.button(this.#rmp.dom.fullscreen, enterFullscreenAccessibleLabel, true, null, 'f');
    // core dom ready
    // do not wait for HLSJS and MPEG-DASH streams to be initialized
    this.#rmp.rmpReadyManagement.init();
  }

}
